<template>
    <div>
        <page-title :text="$t('page_titles.email_templates_page_title')" />
        <v-data-table
            :headers="headers"
            :items="emailTemplatesFiltered"
            :items-per-page-options="[10, 25, 50, 100]"
            :items-length="emailTemplatesFiltered.length"
            :loading="loading"
            class="mt-3"
        >
            <template v-slot:top>
                <div class="h6 ms-3 mb-2 font-weight-medium">
                    {{ $t('admin.email_templates') }}
                </div>
                <div v-if="recipientTypes.length" class="d-flex flex-wrap align-center">
                    <v-select
                        v-model="recipientType"
                        :items="recipientTypes"
                        :label="$t('admin.recipient_type')"
                        class="pl-3 filter-input"
                    >
                        <template v-slot:selection="{item}">
                            {{ $t(`admin.recipientType.${item.raw}`) }}
                        </template>
                        <template v-slot:item="{props, item}">
                            <v-list-item v-bind="props" :title="$t(`admin.recipientType.${item.raw}`)" />
                        </template>
                    </v-select>
                </div>
                <div v-if="$conf.public.account.showCreateNotificationTemplateBtn">
                    <v-btn :to="{name: 'newEmailEditor'}" color="primary" class="ms-3">
                        {{ $t('add') }}
                    </v-btn>
                </div>
            </template>
            <template v-slot:item.notificationType="{item}">
                {{ $t(`admin.notificationType.${item.notificationType}`) }}
            </template>
            <template v-slot:item.lang="{item}">
                {{ item.templateLocales.join(', ') }}
            </template>
            <template v-slot:item.edit="{item}">
                <v-btn
                    variant="text"
                    :to="{
                        name: 'emailEditor',
                        params: {id: item.id},
                    }"
                    icon="mdi-pencil"
                    size="small"
                    color="primary"
                />
            </template>
            <template v-slot:item.actions="{item}">
                <v-btn
                    :disabled="isCopying[item.id]"
                    :loading="isCopying[item.id]"
                    variant="text"
                    size="small"
                    color="primary"
                    class="text-decoration-underline text-lowercase"
                    @click="copyTemplate(item)"
                >
                    {{ $t('copy') }}
                </v-btn>
            </template>
        </v-data-table>
    </div>
</template>

<script lang="ts">
    import {Component, Vue} from 'vue-facing-decorator'
    import AdminNavigation from '~/components/admin/AdminNavigation.vue'
    import PageTitle from '@/components/parts/PageTitle.vue'
    import {CHANGE_LOCALE_EVENT, EventBus} from '@/utils/event-bus'
    import {clone} from '@/utils/helpers'

    @Component({
        components: {
            AdminNavigation,
            PageTitle,
        },
    })
    export class EmailTemplatesPage extends Vue {
        isCopying = {}
        loading = true

        beforeUnmount() {
            EventBus.$off(CHANGE_LOCALE_EVENT, this.load)
        }

        async mounted() {
            await this.load()
            EventBus.$on(CHANGE_LOCALE_EVENT, this.load)
        }

        async load() {
            this.loading = true
            await adminRuntimeStore.loadNotificationTemplates({localeCode: this.locale})
            this.loading = false
        }

        async copyTemplate(item) {
            this.loading = true
            this.isCopying[item.id] = true

            try {
                const originTemplate =
                    this.locale === 'en'
                        ? clone(item)
                        : (await this.$api.notificationTemplates.get({id: item.id, localeCode: 'en'})).templates[0]
                delete originTemplate.id
                delete originTemplate.templateLocales

                await this.$api.notificationTemplates.post({
                    ...originTemplate,
                    localeCode: 'en',
                    notificationType: 'CUSTOM',
                })
                this.$toast.success(this.$t('success_message.saved_successfully'))
                await this.load()
            } catch (e) {
                this.$toast.error(this.$t('error_message.save_error'))
            } finally {
                this.loading = false
                this.isCopying[item.id] = false
            }
        }

        get recipientType() {
            return adminRuntimeStore.notificationTemplatesRecipientType
        }

        set recipientType(val) {
            adminRuntimeStore.SET_NOTIFICATION_TEMPLATES_RECIPIENT_TYPE(val)
        }

        get headers() {
            return [
                {title: '#', value: 'id'},
                {title: this.$t('admin.templateInfo'), value: 'info'},
                {title: this.$t('type'), value: 'notificationType'},
                // {title: this.$t('info'), value: 'info'},
                {title: this.$t('language'), value: 'lang', align: 'center'},
                {title: this.$t('edit'), value: 'edit', align: 'center'},
                {title: this.$t('actions'), value: 'actions', align: 'center'},
            ]
        }

        get emailTemplates() {
            return adminRuntimeStore.notificationTemplates
        }

        get emailTemplatesFiltered() {
            return this.emailTemplates
                .filter(({recipientType}) => recipientType === this.recipientType)
                .sort((a, b) =>
                    this.$t(`admin.notificationType.${a.notificationType}`).localeCompare(
                        this.$t(`admin.notificationType.${b.notificationType}`)
                    )
                )
        }

        get recipientTypes() {
            const types = this.emailTemplates
                .map(({recipientType}) => recipientType)
                .filter(Boolean)
                .sort((a, b) => this.$t(`admin.recipientType.${a}`).localeCompare(this.$t(`admin.recipientType.${b}`)))

            return [...new Set(types)]
        }

        get locale() {
            return this.$i18n.locale
        }
    }

    export default EmailTemplatesPage
</script>

<style lang="scss" scoped>
    td {
        display: flex;
        align-items: center;
    }

    .filter-input {
        max-width: 350px;
    }
</style>
